import dayjs from 'dayjs';
import InfoCard from '@/components/InfoCard';
import { PartyPopper } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { TranslationNS } from '@gokrew/models/constants';
import { useUpcomingHolidays } from '@/hooks/dashboard';
import { useDialog } from '@gokrew/ui/context';
import { formatDate, getWeekDay } from '@gokrew/ui/utility/utility';
import { Holidays } from '../../leave-management/Holidays';
import styles from './PublicHolidaysCard.module.css';
import { CardLoader } from '../CardLoader';
import { ViewAllButton } from '../ViewAllButton';
import { DateBadge } from '../DateBadge';
import { DashboardEmptyState } from '../DashboardEmptyState';

const VISIBLE_COUNT = 3;

export function PublicHolidaysCard({ dataTour }: { dataTour?: string }) {
  const today = new Date();
  const { t, i18n } = useTranslation(TranslationNS.LeaveManagement);
  const { openDialog } = useDialog();
  const { data, isLoading, isError } = useUpcomingHolidays({
    page: 1,
    limit: 99,
    currentDate: formatDate(today, { format: 'iso' }),
    year: today.getFullYear(),
  });
  const holidays = (data?.data.data ?? []).slice(0, VISIBLE_COUNT);

  const openHolidaysDialog = () =>
    openDialog({
      title: t('dialog_titles.public_holidays'),
      content: <Holidays />,
      containerClass: 'sm:w-xl w-full',
    });

  return (
    <InfoCard
      dataTour={dataTour}
      className={styles.card}
      title={t('leave_requests.dashboard_page.upcoming_holidays')}
      action={
        <ViewAllButton
          label={t('leave_requests.dashboard_page.view_all')}
          onClick={openHolidaysDialog}
        />
      }
    >
      {isLoading && <CardLoader />}
      {isError && <div>{t('toast.create.something_went_wrong')}</div>}
      {!isLoading && !isError && holidays.length === 0 && (
        <DashboardEmptyState
          layout="row"
          icon={PartyPopper}
          title={t('leave_requests.dashboard_page.no_upcoming_holidays')}
          description={t(
            'leave_requests.dashboard_page.no_upcoming_holidays_description'
          )}
        />
      )}
      {!isLoading && holidays.length > 0 && (
        <div className={styles.holidaysRow}>
          {holidays.map((item, i) => {
            const isFeatured = i === 0;
            const diff = dayjs(item.leaveDate)
              .startOf('day')
              .diff(dayjs(today).startOf('day'), 'day');
            const weekday = getWeekDay(
              item.leaveDate,
              { weekday: 'long' },
              i18n.language
            );

            let note: string;
            if (diff <= 0) {
              note = t('leave_requests.dashboard_page.today');
            } else {
              note = `${weekday} · ${t('leave_requests.dashboard_page.in_prefix')} ${diff} ${t('leave_requests.dashboard_page.days')}`;
            }

            return (
              <div
                key={item.id}
                className={`${styles.holidayItem} ${isFeatured ? styles.holidayItemFeatured : ''}`}
              >
                <DateBadge
                  dateStr={item.leaveDate}
                  locale={i18n.language}
                  size="lg"
                  inverted={isFeatured}
                />
                <p
                  className={`${styles.holidayName} ${isFeatured ? styles.holidayNameFeatured : ''}`}
                >
                  {item.name}
                </p>
                <p
                  className={`${styles.countdown} ${isFeatured ? styles.countdownFeatured : ''}`}
                >
                  {note}
                </p>
              </div>
            );
          })}
        </div>
      )}
    </InfoCard>
  );
}
